import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import ReactSlider from "react-slider";
import { FocusOn } from "react-focus-on";
import { useFilters } from "../../../hooks/useFilters";
import {
	setCountryDestinations,
	handleDestinationSelections,
	handlePriceRangeChange,
	setDestinationSelectionsInputLabel,
	setStarRatings,
	setSortingItems,
	filterByDestination,
	filterByPrice,
	filterByStarRating,
	sortOffers,
	defaultOrderingTabData,
	handleStarRatingSelection,
	handleSortingOptionSelection,
} from "./util";
import { RCL, RCL as useTranslation } from "../../RCL";
import { Heading } from "../../Heading";
import { RadioGroup } from "../../RadioInput/RadioGroup";
// eslint-disable-next-line import/no-cycle
import { Button } from "../../Button";
// eslint-disable-next-line import/no-cycle
import { Accordion, Card } from "../../TabsAndAccordions";
import { Icon } from "../../Icons/Icons";
import { SelectMultipleDropdown, Dropdown } from "../../Dropdown";
import { PopoverInput } from "../../PopoverInput/PopoverInput";
import { FormattedMoney } from "../../FormattedMoney/FormattedMoney";
import * as styles from "../HotelCardTabs.module.scss";

const Filters = ({ tabOffers, locale, setMatchingOffers, isMobile, gatewayListUI, offerType }) => {
	const [destinationSelections, setDestinationSelections] = useState([]);
	const [priceValue, setPriceValue] = useState(null);
	const [submittedPriceValue, setSubmittedPriceValue] = useState(null);
	const [isDataSubmitted, setIsDataSubmitted] = useState(false);
	// Mobile specific states
	const filterButtonRef = useRef(null);
	const priceRangeRef = useRef(null);
	const [isFiltersOpened, setIsFiltersOpened] = useState(false);
	const [isOlarkChatHidden, setIsOlarkChatHidden] = useState(false);
	const olarkChat = typeof window !== "undefined" ? document.getElementById("olark-wrapper") : null;

	// Destinations
	const resetDestinationFilters = useRef(null);

	const transformedCountryDestinations = setCountryDestinations(tabOffers);

	const handleChangeDestinationSelections = item => {
		setDestinationSelections(
			handleDestinationSelections(item, destinationSelections, transformedCountryDestinations)
		);
	};

	const destinationSelectionsInputLabel = RCL({
		searchKey: "destination-filter-swg",
	});

	const selectedLabel = RCL({ searchKey: "selected" });

	const handleDestinationSelectionsInputLabel = () =>
		setDestinationSelectionsInputLabel(
			destinationSelections,
			destinationSelectionsInputLabel,
			selectedLabel
		);

	const handleClearDestinations = () => {
		setDestinationSelections([]);
	};

	// Price Ranges
	const priceRanges = tabOffers.map(price => price.Price).sort((a, b) => a - b);

	// For react slider default values
	const [selectedPriceRange, setSelectedPriceRange] = useState([
		priceRanges[0],
		priceRanges[priceRanges.length - 1],
	]);

	const handlePriceRange = (val1, val2, min, max) => {
		setSelectedPriceRange([val1, val2]);
		setPriceValue(handlePriceRangeChange(val1, val2, min, max, submittedPriceValue));
	};

	const resetSlider = () => {
		setSelectedPriceRange([priceRanges[0], priceRanges[priceRanges.length - 1]]);
	};

	useEffect(() => {
		if (submittedPriceValue) {
			setSelectedPriceRange(submittedPriceValue);
		}

		if (priceValue === null) {
			resetSlider();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Star Ratings
	const starRatings = setStarRatings(offerType, tabOffers);

	const [starRatingSelection, setStarRatingSelection] = useState(
		starRatings?.[starRatings?.length - 1]
	);

	const handleSelectedStarRating = value => {
		setStarRatingSelection(handleStarRatingSelection(value, starRatings));
	};

	const filterStarRating = useTranslation({ searchKey: "filter-star-rating" });

	// Sorting items
	const sortingItemLabels = {
		recommended: useTranslation({ searchKey: "recommended" }),
		priceLowToHigh: useTranslation({ searchKey: "price-low-to-high" }),
		priceHighToLow: useTranslation({ searchKey: "price-high-to-low" }),
		starRatingLowToHigh: useTranslation({
			searchKey: "star-rating-low-to-high",
		}),
		starRatingHighToLow: useTranslation({
			searchKey: "star-rating-high-to-low",
		}),
		hotelAz: useTranslation({ searchKey: "hotel-name-a-to-z" }),
		hotelZa: useTranslation({ searchKey: "hotel-name-z-to-a" }),
		departureEarliest: useTranslation({
			searchKey: "departure-date-earliest-available",
		}),
	};

	const sortingItems = setSortingItems(offerType, sortingItemLabels);

	const [sortingOptionSelection, setSortingOptionSelection] = useState(sortingItems[0]);

	const handleSortingOptionChange = value => {
		setSortingOptionSelection(handleSortingOptionSelection(value, sortingItems));
	};

	// Applying filters
	const { hasFiltersChanged, setAppliedFilters, numberOfFilters, appliedFilters } = useFilters({
		destinationSelections,
		starRatingSelection,
		priceValue,
		sortingOptionSelection,
	});

	const handleApplySubmit = (offers, filters) => {
		let newOffers = offers ? [...offers] : [];

		newOffers = filterByDestination(newOffers, filters.destinationSelections);
		newOffers = filterByPrice(newOffers, filters.priceValue);
		newOffers = filterByStarRating(newOffers, filters.starRatingSelection);
		newOffers = sortOffers(newOffers, filters.sortingOptionSelection?.id, locale);

		setMatchingOffers(newOffers);
		setSubmittedPriceValue(filters.priceValue);
		setIsDataSubmitted(true);
	};

	// Clearing filters
	const handleClearAllFilters = () => {
		setDestinationSelections([]);
		setPriceValue(null);
		setStarRatingSelection(starRatings?.[starRatings?.length - 1]);
		setSortingOptionSelection(sortingItems[0]);

		setMatchingOffers(defaultOrderingTabData(tabOffers, locale));

		if (typeof resetDestinationFilters?.current === "function") {
			resetDestinationFilters.current();
		}
		setIsDataSubmitted(false);
	};

	useEffect(() => {
		setAppliedFilters({
			destinationSelections,
			starRatingSelection,
			priceValue,
			sortingOptionSelection,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Close mobile filters menu
	const unmountTrap = () => {
		setIsFiltersOpened(false);

		// On close menu, set focus back to hamburger menu button
		if (filterButtonRef.current) {
			setTimeout(() => {
				filterButtonRef.current.focus();
			}, 0);
		}

		setTimeout(() => {
			window.dispatchEvent(new Event("resize", { bubbles: true }));

			if (window.olark && typeof window.olark === "function" && olarkChat && isOlarkChatHidden) {
				window.olark("api.box.show");
			}
		}, 500);
	};

	const handleToggleFilter = () => {
		setIsFiltersOpened(!isFiltersOpened);

		// Disable EA and chat popups from the bottom of the page while the filters are open

		setTimeout(() => {
			window.dispatchEvent(new Event("resize", { bubbles: true }));

			if (window.olark && typeof window.olark === "function" && olarkChat) {
				if (getComputedStyle(olarkChat).display !== "none") {
					window.olark("api.box.hide");

					setIsOlarkChatHidden(true);
				} else {
					setIsOlarkChatHidden(false);
				}
			}
		}, 50);
	};

	// Hide "SORT AND FILTER" if very small screen, just showing icon
	const [hideMenuButtonText, setHideMenuButtonText] = useState(false);

	useEffect(() => {
		const handleResize = () => {
			setHideMenuButtonText(window.innerWidth <= 355);
		};
		window.addEventListener("resize", handleResize);
		handleResize();
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	// Desktop specific UI
	if (!isMobile) {
		return (
			<div className={styles.filters}>
				<SelectMultipleDropdown
					id="destinations"
					label={destinationSelections && RCL({ searchKey: "destination-filter-swg" })}
					handleDynamicInputLabel={handleDestinationSelectionsInputLabel}
					data={transformedCountryDestinations}
					values={destinationSelections}
					onSelectChange={handleChangeDestinationSelections}
					resetFiltersFnRef={resetDestinationFilters}
					handleClearAll={handleClearDestinations}
				/>
				<PopoverInput
					defaultCloseIcon={false}
					disableLockScroll={true}
					theme="filters"
					label={priceValue && RCL({ searchKey: "price-range" })}
					icon={<Icon name="arrow-thick-down" className={cx(styles.iconDropdown)} />}
					value={
						// eslint-disable-next-line no-nested-ternary
						priceValue !== null
							? `${FormattedMoney({
									number: priceValue[0],
									local: locale,
							  })} - ${FormattedMoney({
									number: priceValue[1],
									local: locale,
							  })}`
							: RCL({ searchKey: "price-range" })
					}
					id="price-range"
					className={priceValue !== null ? styles.priceRangeActive : styles.priceRange}
					inputClassName={styles.priceRangeInput}
					popoverClassName={styles.priceRangePopover}>
					<div className={styles.priceRangeInner}>
						<div aria-describedby="priceRange" className={styles.priceRangeText}>
							<RCL searchKey="price-per-person-cad" /> (
							<FormattedMoney number="CAD" local={locale} />)
						</div>
						<div className={styles.priceRangeInputWrapper}>
							<ReactSlider
								className="horizontal-slider"
								thumbClassName={styles.priceRangeThumb}
								trackClassName={styles.priceRangeTrack}
								value={selectedPriceRange}
								ariaLabelledby={["first-slider-label", "second-slider-label"]}
								ariaValuetext={`${RCL({
									searchKey: "thumb-value-between",
								})} ${priceRanges[0]}  ${RCL({ searchKey: "and" })} ${priceRanges[1]}`}
								onChange={value => {
									handlePriceRange(
										value[0],
										value[1],
										priceRanges[0],
										priceRanges[priceRanges.length - 1]
									);
								}}
								renderThumb={(props, state) => (
									// eslint-disable-next-line react/jsx-props-no-spreading
									<div {...props}>{state.valueNow}</div>
								)}
								pearling
								min={priceRanges[0]}
								max={priceRanges[priceRanges.length - 1]}
								minDistance={10}
							/>
							{/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
							<label id="first-slider-label" className={styles.priceRangeValue}>
								<FormattedMoney number={selectedPriceRange[0]} local={locale} />
							</label>
							{/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
							<label id="second-slider-label" className={styles.priceRangeValue}>
								<FormattedMoney number={selectedPriceRange[1]} local={locale} />
							</label>
						</div>
					</div>
				</PopoverInput>
				{offerType === "packages" && (
					<div className={styles.filtersDropdownWrapper}>
						<Dropdown
							id="starRating"
							label={
								starRatingSelection.value !== starRatings[starRatings.length - 1].value &&
								filterStarRating
							}
							isRadioOption={true}
							initialSelectedItem={appliedFilters?.starRatingSelection}
							placeholder={
								appliedFilters?.starRatingSelection.value ===
									starRatings[starRatings.length - 1].value && filterStarRating
							}
							selectedItem={starRatingSelection}
							isOptionChanged={
								appliedFilters?.starRatingSelection.value !==
								starRatings[starRatings.length - 1].value
							}
							onChange={option => {
								if (option !== starRatingSelection) setStarRatingSelection(option);
							}}
							data={[{ items: starRatings }]}
						/>
					</div>
				)}
				<div className={cx(styles.filtersDropdownWrapper, styles.filtersDropdownWrapperSort)}>
					<Dropdown
						id="sortingFilter"
						isRadioOption={true}
						initialSelectedItem={appliedFilters?.sortingOptionSelection}
						placeholder={`${RCL({ searchKey: "sort-by" })}`}
						label={
							sortingOptionSelection.value !== sortingItems[0].value &&
							RCL({ searchKey: "sort-by" })
						}
						showDefaultSelection={true}
						isOptionChanged={appliedFilters?.sortingOptionSelection.value !== sortingItems[0].value}
						className={styles.sortingDropdown}
						selectedItem={sortingOptionSelection}
						onChange={option => {
							if (option !== sortingOptionSelection) setSortingOptionSelection(option);
						}}
						data={[
							{
								items: sortingItems,
							},
						]}
					/>
				</div>
				<Button
					onClick={() => {
						setAppliedFilters({
							destinationSelections,
							starRatingSelection,
							priceValue,
							sortingOptionSelection,
						});
						handleApplySubmit(tabOffers, {
							destinationSelections,
							starRatingSelection,
							priceValue,
							sortingOptionSelection,
						});
					}}
					disabled={!hasFiltersChanged}>
					{`${RCL({ searchKey: "apply" })}${numberOfFilters > 0 ? ` (${numberOfFilters})` : ""}`}
				</Button>
				<Button
					onClick={() => {
						setAppliedFilters({
							destinationSelections: [],
							starRatingSelection: starRatings?.[starRatings?.length - 1],
							priceValue: null,
							sortingOptionSelection: sortingItems[0],
						});
						handleClearAllFilters();
						resetSlider();
					}}
					noStyle={true}
					className={styles.reset}
					disabled={!isDataSubmitted}>
					<RCL searchKey="reset-filter-swg" />
				</Button>
			</div>
		);
	}

	// Mobile specific UI
	return (
		<>
			<div className={styles.mobileDropdownWrapper}>
				<div className={styles.mobileDropdownWrapperInner}>
					{gatewayListUI}
					{tabOffers.length > 6 && (
						<Button
							label={RCL({ searchKey: "sort-and-filter" })}
							noStyle={true}
							className={styles.filtersMenuButton}
							onClick={handleToggleFilter}
							aria-label={RCL({ searchKey: "a11y-open-menu" })}
							aria-expanded={isFiltersOpened}
							aria-controls="main-menu-nav"
							ref={filterButtonRef}>
							<span className={hideMenuButtonText ? "sr-only" : ""}>
								{RCL({ searchKey: "sort-and-filter" })}
							</span>
							<Icon name="filter" />
						</Button>
					)}
				</div>
			</div>
			{tabOffers.length > 6 && (
				<nav aria-label="filters" className={cx(styles.filters)}>
					<div
						id="filters-menu-nav"
						className={cx(styles.wrapper, isFiltersOpened && styles.wrapperOpen)}>
						<FocusOn
							enabled={isFiltersOpened}
							shouldIgnore={el => !(el.id === "gatsby-focus-wrapper" && el.tabIndex === -1)}
							onClickOutside={handleToggleFilter}
							shards={[priceRangeRef]}
							onEscapeKey={handleToggleFilter}
							className={styles.container}
							gapMode="padding">
							{isFiltersOpened && (
								<div className={styles.filtersMenu}>
									<div className={styles.filtersMenuHeader}>
										<Heading type="h3" as="h4" className={styles.filtersMenuTitle} color="rock">
											{RCL({ searchKey: "sort-and-filter" })}
										</Heading>
										{/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
										<button type="button" onClick={unmountTrap}>
											<Icon name="close--n" />
										</button>
									</div>
									<div className={styles.filtersMenuSorting}>
										<Accordion variant="merch">
											<Card eventKey="sorting-options">
												<Card.Toggle> {RCL({ searchKey: "sort" })}</Card.Toggle>
												<Card.Panel>
													<RadioGroup
														name="sortingOptions"
														displayStacked={true}
														options={sortingItems}
														selectedOption={
															// eslint-disable-next-line no-nested-ternary
															sortingOptionSelection.value
																? sortingOptionSelection.value
																: sortingItems[0].value
														}
														onChange={value => handleSortingOptionChange(value)}
													/>
												</Card.Panel>
											</Card>
										</Accordion>
									</div>
									<div className={styles.filtersMenuDestinations}>
										<Heading as="h3" className={styles.filtersMenuDestinationsTitle}>
											{RCL({ searchKey: "destination-filter-swg" })}
										</Heading>
										{transformedCountryDestinations.map(destinations => (
											<>
												{transformedCountryDestinations.filter(
													x => x.title !== destinations.countryName
												) ? (
													<div key={destinations.value} className={styles.checkbox}>
														<input
															name="destinations"
															type="checkbox"
															id={destinations.title}
															value={destinations.title}
															checked={destinationSelections.some(
																selection => selection.value === destinations.title
															)}
															onChange={
																() =>
																	handleChangeDestinationSelections({
																		label: destinations.title,
																		title: destinations.title,
																		value: destinations.title,
																	})
																// eslint-disable-next-line react/jsx-curly-newline
															}
														/>
														<label
															htmlFor={destinations.title}
															className={styles.filtersMenuDestinationsCountry}>
															{destinations.title}
														</label>
													</div>
												) : (
													<div className={styles.filtersMenuCountryTitle}>{destinations.title}</div>
												)}
												{/* eslint-disable-next-line array-callback-return, consistent-return */}
												{destinations.items.map(destination => {
													if (destination.label !== destination.countryName) {
														return (
															<div key={destination.value} className={styles.checkbox}>
																<input
																	name="destinations"
																	type="checkbox"
																	id={destination.label}
																	value={destination.value}
																	checked={destinationSelections.some(
																		selection => selection.value === destination.value
																	)}
																	onChange={() => handleChangeDestinationSelections(destination)}
																/>
																<label htmlFor={destination.label}>{destination.label}</label>
															</div>
														);
													}
												})}
											</>
										))}
										<button
											type="button"
											className={styles.filtersMenuClear}
											onClick={() => setDestinationSelections([])}>
											{RCL({ searchKey: "clear-all" })}
										</button>
									</div>
									<div className={styles.filtersMenuPriceRange} ref={priceRangeRef}>
										<div className={styles.filtersMenuPriceRangeInner}>
											<div aria-describedby="priceRange" className={styles.priceRangeText}>
												<RCL searchKey="price-per-person-cad" /> (CAD)
											</div>
											<div className={styles.priceRangeInputWrapper}>
												<ReactSlider
													className="horizontal-slider"
													thumbClassName={styles.priceRangeThumb}
													trackClassName={styles.priceRangeTrack}
													value={selectedPriceRange}
													ariaLabelledby={["first-slider-label", "second-slider-label"]}
													ariaValuetext={`${RCL({
														searchKey: "thumb-value-between",
													})} ${priceRanges[0]}
												${RCL({ searchKey: "and" })} ${priceRanges[1]}`}
													onChange={value => {
														handlePriceRange(
															value[0],
															value[1],
															priceRanges[0],
															priceRanges[priceRanges.length - 1]
														);
													}}
													renderThumb={(props, state) => (
														// eslint-disable-next-line react/jsx-props-no-spreading
														<div {...props}>{state.valueNow}</div>
													)}
													pearling
													min={priceRanges[0]}
													max={priceRanges[priceRanges.length - 1]}
													minDistance={10}
												/>
												{/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
												<label id="first-slider-label" className={styles.priceRangeValue}>
													<FormattedMoney number={selectedPriceRange[0]} local={locale} />
												</label>
												{/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
												<label id="second-slider-label" className={styles.priceRangeValue}>
													<FormattedMoney number={selectedPriceRange[1]} local={locale} />
												</label>
											</div>
										</div>
									</div>
									{offerType === "packages" && (
										<div className={styles.filtersMenuStarRating}>
											<Heading as="h3" className={styles.filtersMenuStarRatingTitle}>
												{filterStarRating}
											</Heading>

											<div className={styles.filtersMenuStarRatingOption}>
												<RadioGroup
													name="starRating"
													displayStacked={true}
													options={starRatings}
													selectedOption={
														starRatingSelection?.value ?? starRatings[starRatings.length - 1].value
													}
													onChange={value => handleSelectedStarRating(value)}
												/>
											</div>
										</div>
									)}
									<div className={styles.filtersMenuFormControl}>
										<div className={styles.filtersMenuFormControlInner}>
											<Button
												onClick={() => {
													setAppliedFilters({
														destinationSelections: [],
														starRatingSelection: starRatings?.[starRatings?.length - 1],
														priceValue: null,
														sortingOptionSelection: sortingItems[0],
													});
													handleClearAllFilters();
													resetSlider();
												}}
												noStyle={true}
												className={styles.reset}
												disabled={!isDataSubmitted}>
												{RCL({ searchKey: "reset-filter-swg" })}
											</Button>
											<Button
												className={styles.filtersMenuApply}
												onClick={() => {
													setAppliedFilters({
														destinationSelections,
														starRatingSelection,
														priceValue,
														sortingOptionSelection,
													});
													handleApplySubmit(tabOffers, {
														destinationSelections,
														starRatingSelection,
														priceValue,
														sortingOptionSelection,
													});
													unmountTrap();
												}}
												disabled={!hasFiltersChanged}>
												{`${RCL({ searchKey: "apply" })} ${
													numberOfFilters > 0 ? `(${numberOfFilters})` : ""
												}`}
											</Button>
										</div>
									</div>
								</div>
							)}
						</FocusOn>
					</div>
				</nav>
			)}
		</>
	);
};

Filters.propTypes = {
	tabOffers: PropTypes.object.isRequired,
	locale: PropTypes.string.isRequired,
	setMatchingOffers: PropTypes.array.isRequired,
	isMobile: PropTypes.bool.isRequired,
	gatewayListUI: PropTypes.node.isRequired,
	offerType: PropTypes.string.isRequired,
};

export default Filters;
export { Filters };
